import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Handles the parts search functionality
 * Filters products by title, SKU, barcode, or tags
 *
 * @extends {Component}
 */
class PartsSearchComponent extends Component {
  requiredRefs = ['searchInput', 'resetButton'];
  #isSearching = false;
  /** @type {number} Bumps on each new search; stale async work must not touch the DOM */
  #searchGeneration = 0;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  /** @type {MutationObserver | null} */
  #inputObserver = null;
  /**
   * True while we're waiting for the theme's section re-render (triggered by a facet filter
   * change) to finish so we can re-apply the search on top of it.
   */
  #awaitingFilterResult = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #awaitingFilterTimer = undefined;

  /**
   * Hides product grid and empty state
   */
  #hideResults() {
    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) return;
    const productGrid = resultsList.querySelector('.product-grid');
    const emptyState = resultsList.querySelector('.main-collection-grid__empty');
    if (productGrid instanceof HTMLElement) {
      productGrid.style.display = 'none';
    }
    if (emptyState instanceof HTMLElement) {
      emptyState.style.display = 'none';
    }
  }

  /**
   * Shows the reset button
   */
  #showResetButton() {
    const { resetButton } = this.refs;
    if (resetButton instanceof HTMLElement) {
      resetButton.hidden = false;
    }
  }

  /**
   * Hides the reset button
   */
  #hideResetButton() {
    const { resetButton } = this.refs;
    if (resetButton instanceof HTMLElement) {
      resetButton.hidden = true;
    }
  }

  /**
   * Resets the search input and clears the search
   */
  resetSearch = () => {
    const { searchInput } = this.refs;
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
      searchInput.focus();
      this.#hideResetButton();

      // Clear search by removing 'q' parameter from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('q');
      window.location.href = url.toString();
    }
  };

  /**
   * Gets search query from URL or input
   * @param {HTMLInputElement} searchInput - The search input element
   * @returns {string} The search query
   */
  #getQuery(searchInput) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('q') || searchInput.value.trim();
  }

  /**
   * Initializes the search component
   */
  connectedCallback() {
    super.connectedCallback();
    const { searchInput } = this.refs;
    if (!(searchInput instanceof HTMLInputElement)) return;

    // Get initial search query from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q') || '';

    if (initialQuery) {
      searchInput.value = initialQuery;
      // Show reset button if there's initial query
      this.#showResetButton();
      // Show loading animation
      // Products are already hidden by inline script/style in product-grid.liquid
      this.#showLoadingAnimation(true);
      // Perform search on page load if query is present
      // Use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        const trimmedQuery = initialQuery.trim();
        if (trimmedQuery) {
          this.#filterProducts(trimmedQuery);
        }
      }, 100);
    } else {
      // If no query, ensure empty state is hidden and grid is shown
      setTimeout(() => {
        const resultsList = document.querySelector('#ResultsList');
        if (resultsList) {
          const productGrid = resultsList.querySelector('.product-grid');
          const emptyState = resultsList.querySelector('.main-collection-grid__empty');

          // If grid has products, show it and hide empty state
          if (productGrid instanceof HTMLElement) {
            const hasProducts = productGrid.querySelectorAll('.product-grid__item').length > 0;
            if (hasProducts) {
              productGrid.style.display = '';
              if (emptyState instanceof HTMLElement) {
                emptyState.style.display = 'none';
              }
            }
          }
        }
      }, 100);
    }

    // Listen for category filter changes
    window.addEventListener('category-filter-changed', () => {
      const query = searchInput.value.trim().toLowerCase();
      if (query) {
        this.#filterProducts(query);
      }
    });

    // Hide products immediately when filters are clicked (before FilterUpdate event)
    const facetsForm = searchInput.closest('form');
    if (facetsForm instanceof HTMLFormElement) {
      // Listen for clicks on filter inputs (checkboxes, radio buttons)
      facetsForm.addEventListener('click', (e) => {
        const target = e.target;
        // Check if clicked element is a filter input (not the search input itself)
        if (
          target instanceof HTMLInputElement &&
          (target.type === 'checkbox' || target.type === 'radio') &&
          (target.name.startsWith('filter.') || target.name === 'filter.v.availability') &&
          target !== searchInput
        ) {
          const query = this.#getQuery(searchInput);
          if (query) {
            this.#isSearching = true;
            if (searchInput.value.trim() !== query) {
              searchInput.value = query;
            }
            this.#hideResults();
            this.#showLoadingAnimation(true);
          }
        }
      }, true); // Use capture phase to run before other handlers
    }

    // Listen for filter updates (e.g., "Only items in stock" checkbox)
    window.addEventListener(ThemeEvents.FilterUpdate, (event) => {
      if (event instanceof FilterUpdateEvent) {
        const query = this.#getQuery(searchInput);
        if (query) {
          this.#isSearching = true;
          this.#hideResults();
          this.#showLoadingAnimation(true);

          // Arm the post-render guard: if the theme's section re-render completes
          // AFTER our search (race condition), the MutationObserver will re-apply
          // the search on top of the freshly morphed DOM.
          this.#awaitingFilterResult = true;
          clearTimeout(this.#awaitingFilterTimer);
          this.#awaitingFilterTimer = setTimeout(() => {
            this.#awaitingFilterResult = false;
          }, 2000);

          setTimeout(() => {
            this.#filterProducts(query);
          }, 100);
        }
      }
    });

    // Use MutationObserver to hide products immediately when sectionRenderer updates DOM
    const resultsList = document.querySelector('#ResultsList');
    if (resultsList) {
      this.#mutationObserver = new MutationObserver((mutations) => {
        // Race-condition guard: the theme's facet section re-render (e.g. availability filter)
        // may complete AFTER our search and overwrite the filtered grid with all products.
        // Detect this by watching for product items being added while we're no longer searching.
        if (this.#awaitingFilterResult && !this.#isSearching) {
          const query = this.#getQuery(searchInput);
          if (query) {
            const themeAddedProducts = mutations.some((m) =>
              m.type === 'childList' &&
              Array.from(m.addedNodes).some(
                (n) => n instanceof HTMLElement && n.classList.contains('product-grid__item')
              )
            );
            if (themeAddedProducts) {
              this.#awaitingFilterResult = false;
              clearTimeout(this.#awaitingFilterTimer);
              this.#isSearching = true;
              this.#hideResults();
              this.#showLoadingAnimation(true);
              this.#filterProducts(query);
              return;
            }
          }
        }

        if (!this.#isSearching) return;

        const query = this.#getQuery(searchInput);
        if (!query) {
          this.#isSearching = false;
          return;
        }

        for (const mutation of mutations) {
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            const productGrid = resultsList.querySelector('.product-grid');
            if (productGrid instanceof HTMLElement && productGrid.style.display !== 'none') {
              this.#hideResults();
            }
          }
        }
      });

      // Start observing
      if (this.#mutationObserver) {
        this.#mutationObserver.observe(resultsList, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }
    }

    // Use MutationObserver to restore input value if it was lost during section update
    this.#inputObserver = new MutationObserver(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = urlParams.get('q');

      // If there's a query in URL but input is empty, restore it
      if (urlQuery && (!searchInput.value || searchInput.value.trim() !== urlQuery)) {
        searchInput.value = urlQuery;
        // Show reset button if value is restored
        if (urlQuery.trim().length > 0) {
          this.#showResetButton();
        }
      } else if (searchInput.value && searchInput.value.trim().length > 0) {
        // Show reset button if input has value
        this.#showResetButton();
      } else if (!searchInput.value || searchInput.value.trim().length === 0) {
        // Hide reset button if input is empty
        this.#hideResetButton();
      }
    });

    // Observe the input and its parent form for changes
    if (searchInput) {
      this.#inputObserver.observe(searchInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Also observe the form in case it gets replaced
      if (facetsForm instanceof HTMLFormElement) {
        this.#inputObserver.observe(facetsForm, {
          childList: true,
          subtree: true
        });
      }
    }

    // Debounce search input
    const debouncedSearch = debounce((query) => {
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        this.#filterProducts(trimmedQuery);
        this.#updateURL(trimmedQuery);
      } else {
        // Clear search if input is empty
        const url = new URL(window.location.href);
        url.searchParams.delete('q');
        window.location.href = url.toString();
      }
    }, 300);

    searchInput.addEventListener('input', (e) => {
      if (e.target instanceof HTMLInputElement) {
        const query = e.target.value;
        // Show/hide reset button based on input value
        if (query.trim().length > 0) {
          this.#showResetButton();
        } else {
          this.#hideResetButton();
        }
        debouncedSearch(query);
      }
    });

    // Handle form submission
    const form = searchInput.closest('form');
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const query = searchInput.value.trim();
        if (query) {
          this.#filterProducts(query);
          this.#updateURL(query);
        } else {
          const url = new URL(window.location.href);
          url.searchParams.delete('q');
          window.location.href = url.toString();
        }
      });
    }
  }

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
    if (this.#inputObserver) {
      this.#inputObserver.disconnect();
      this.#inputObserver = null;
    }
    this.#isSearching = false;
    this.#awaitingFilterResult = false;
    clearTimeout(this.#awaitingFilterTimer);
  }

  /**
   * True if any variant's SKU or barcode contains the query (case-insensitive).
   * @param {any} product - Product object from /products/handle.js
   * @param {string} queryLower - Lowercased search string
   * @returns {boolean}
   */
  #variantsMatchQuery(product, queryLower) {
    const variants = product?.variants;
    if (!Array.isArray(variants)) return false;
    for (const v of variants) {
      const sku = String(v?.sku ?? '').toLowerCase();
      const barcode = String(v?.barcode ?? '').toLowerCase();
      if (sku.includes(queryLower) || barcode.includes(queryLower)) return true;
    }
    return false;
  }

  /**
   * Single-token queries without spaces — typical SKU / part code (not multi-word search).
   * @param {string} trimmedQuery
   * @returns {boolean}
   */
  #isSkuLikeQuery(trimmedQuery) {
    const t = trimmedQuery.trim();
    if (t.length < 3) return false;
    if (/\s/.test(t)) return false;
    return /^[\w.-]+$/i.test(t);
  }

  /**
   * Filters products using Shopify's native collection tag URL (/collections/handle/tag).
   * Returns complete, server-side exact-match results — bypasses global search limitations.
   * @param {string} tag - The tag to filter by (will be lowercased for URL)
   * @param {number} gen - Search generation guard
   */
  async #filterByTag(tag, gen) {
    const tagLower = tag.toLowerCase();

    const currentPath = window.location.pathname;
    const collectionMatch = currentPath.match(/\/collections\/([^/]+)/);
    const collectionHandle = collectionMatch ? collectionMatch[1] : 'parts';

    const resultsListElement = document.querySelector('results-list');
    const sectionElement = resultsListElement?.closest('.shopify-section');
    const normalizedSectionId = sectionElement?.id || '';
    const sectionId = resultsListElement?.getAttribute('section-id') || '';

    const tagUrl = `/collections/${collectionHandle}/${encodeURIComponent(tagLower)}`;
    const allItems = [];
    const seenIds = new Set();
    const parser = new DOMParser();

    // Pre-validate: when a tag doesn't exist, Shopify returns the full unfiltered collection
    // instead of an empty result, making it impossible to detect after the fact.
    // A lightweight suggest call on the tag field avoids the expensive section render loop
    // for non-existent tags (e.g. "sdfsdf", "APE110").
    try {
      const tagCheckUrl = new URL('/search/suggest.json', window.location.origin);
      tagCheckUrl.searchParams.set('q', tagLower);
      tagCheckUrl.searchParams.set('resources[type]', 'product');
      tagCheckUrl.searchParams.set('resources[limit]', '1');
      tagCheckUrl.searchParams.set('resources[options][fields]', 'tag');
      const tagCheckResp = await fetch(tagCheckUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (tagCheckResp.ok && gen === this.#searchGeneration) {
        const tagCheckData = await tagCheckResp.json();
        const tagged = tagCheckData?.resources?.results?.products || [];
        if (tagged.length === 0) {
          this.#showLoadingAnimation(false);
          this.#showEmptyState(true);
          this.#isSearching = false;
          return;
        }
      }
    } catch { /* if validation fails, proceed with section render */ }

    if (gen !== this.#searchGeneration) return;

    for (let page = 1; page <= 10; page++) {
      if (gen !== this.#searchGeneration) return;

      const renderUrl = new URL(tagUrl, window.location.origin);
      // Preserve active filters (availability, sort, etc.) from the current URL
      const currentParams = new URL(window.location.href).searchParams;
      currentParams.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by') {
          renderUrl.searchParams.set(key, value);
        }
      });
      renderUrl.searchParams.set('page', String(page));
      renderUrl.searchParams.set('per_page', '50');

      let sectionHTML;
      try {
        sectionHTML = await sectionRenderer.getSectionHTML(normalizedSectionId, false, renderUrl);
      } catch {
        break;
      }

      if (!sectionHTML) break;  // null (e.g. 404) → stop paging, fall through to allItems check
      if (gen !== this.#searchGeneration) return;

      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const newResultsList = doc.querySelector('#ResultsList');
      const grid = /** @type {HTMLElement | null} */ (newResultsList?.querySelector('.product-grid') || doc.querySelector('.product-grid'));
      if (!grid) break;

      const items = Array.from(grid.querySelectorAll('.product-grid__item'));
      let newCount = 0;

      for (const item of items) {
        const id = item.getAttribute('data-product-id');
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          allItems.push({ item, productId: id });
          newCount++;
        }
      }

      if (newCount === 0) break;
    }

    if (gen !== this.#searchGeneration) return;

    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) { this.#showLoadingAnimation(false); this.#isSearching = false; return; }

    const productGrid = /** @type {HTMLElement | null} */ (resultsList.querySelector('.product-grid'));
    const emptyState = /** @type {HTMLElement | null} */ (resultsList.querySelector('.main-collection-grid__empty'));

    if (allItems.length === 0) {
      if (gen !== this.#searchGeneration) return;
      // No products found under this tag — suggest already ran before this call,
      // so there's nothing left to try.
      this.#showLoadingAnimation(false);
      this.#showEmptyState(true);
      this.#isSearching = false;
      return;
    }

    if (emptyState) emptyState.style.display = 'none';

    if (productGrid) {
      productGrid.innerHTML = '';
      allItems.forEach(({ item, productId }, index) => {
        const cloned = item.cloneNode(true);
        if (cloned instanceof HTMLElement) {
          cloned.setAttribute('id', `${sectionId}-${productId}`);
          cloned.className = `product-grid__item product-grid__item--${index}`;
          productGrid.appendChild(cloned);
        }
      });

      const visibleCount = allItems.length;
      this.#applyClientSearchPaginationState(productGrid, visibleCount);

      const perPage = parseInt(productGrid.dataset.displayPerPage || '12', 10);
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination instanceof HTMLElement) {
        customPagination.style.setProperty('display', visibleCount <= perPage ? 'none' : '', 'important');
      }

      productGrid.style.setProperty('display', '', 'important');
    }

    document.querySelector('style[data-parts-search-hide]')?.remove();
    this.#isSearching = false;
    this.#showLoadingAnimation(false);
    this.#reinitializeComponents();
  }

  /**
   * Exact match on handle, full title, or any variant SKU/barcode.
   * @param {any} product
   * @param {string} handle
   * @param {string} queryLower
   * @returns {boolean}
   */
  #hasExactIdentifierMatch(product, handle, queryLower) {
    const q = queryLower.trim();
    if (!q) return false;
    if ((handle || '').toLowerCase() === q) return true;
    if ((product.title || '').trim().toLowerCase() === q) return true;
    const variants = product?.variants;
    if (!Array.isArray(variants)) return false;
    for (const v of variants) {
      if (String(v?.sku ?? '').trim().toLowerCase() === q) return true;
      if (String(v?.barcode ?? '').trim().toLowerCase() === q) return true;
    }
    return false;
  }

  /**
   * Filters products based on search query using Shopify server-side search
   * @param {string} query - The search query
   */
  async #filterProducts(query) {
    this.#isSearching = true;
    const trimmedQuery = query ? query.trim() : '';

    if (!trimmedQuery) {
      const url = new URL(window.location.href);
      url.searchParams.delete('q');
      window.location.href = url.toString();
      return;
    }

    this.#showLoadingAnimation(true);

    const gen = ++this.#searchGeneration;

    // For SKU-like queries: check article/title match first (suggest API), then
    // fall back to machine tag search. This ensures "J80" finds part "J80.001"
    // before showing all parts for the J80 machine.
    if (this.#isSkuLikeQuery(trimmedQuery)) {
      await this.#filterBySuggest(trimmedQuery, gen);
      return;
    }

    try {
      // Fetch ALL pages of search results to avoid missing products past page 1
      const parser = new DOMParser();
      const seenProductIds = new Set();
      const productItems = [];

      for (let page = 1; page <= 10; page++) {
        const searchUrl = new URL('/search', window.location.origin);
        searchUrl.searchParams.set('q', trimmedQuery);
        searchUrl.searchParams.set('type', 'product');
        searchUrl.searchParams.set('options[unavailable_products]', 'last');
        searchUrl.searchParams.set('options[prefix]', 'last');
        if (page > 1) searchUrl.searchParams.set('page', String(page));

        const response = await fetch(searchUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
        });

        if (!response.ok) break;
        if (gen !== this.#searchGeneration) return;

        const html = await response.text();
        const doc = parser.parseFromString(html, 'text/html');

        let pageItems = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));
        if (pageItems.length === 0) pageItems = Array.from(doc.querySelectorAll('li[data-product-id]'));
        if (pageItems.length === 0) pageItems = Array.from(doc.querySelectorAll('[data-product-id]'));
        if (pageItems.length === 0) {
          const resultsList = doc.querySelector('#ResultsList, results-list, .product-grid');
          if (resultsList) pageItems = Array.from(resultsList.querySelectorAll('[data-product-id], .product-grid__item'));
        }

        let newOnPage = 0;
        for (const item of pageItems) {
          const id = item.getAttribute('data-product-id');
          if (id && !seenProductIds.has(id)) {
            seenProductIds.add(id);
            productItems.push(item);
            newOnPage++;
          }
        }

        // No new items on this page — end of results
        if (newOnPage === 0) break;

        // No "next page" link — this was the last page
        const hasNext = doc.querySelector('[rel="next"], .pagination__next, [aria-label="Next page"], [aria-label="Next"]');
        if (!hasNext) break;
      }

      if (productItems.length === 0) {
        if (gen !== this.#searchGeneration) return;
        await this.#updateResultsList([], trimmedQuery, gen);
        return;
      }

      let partsProducts = [];

      // Get all product handles and IDs first
      const productData = [];

      for (const item of productItems) {
        const productId = item.getAttribute('data-product-id');
        if (!productId) continue;

        const productHandle = this.#getProductHandleFromItem(item);
        if (productHandle) {
          productData.push({ item, productId, handle: productHandle });
        }
      }

      if (productData.length === 0) {
        if (gen !== this.#searchGeneration) return;
        await this.#updateResultsList([], trimmedQuery, gen);
        return;
      }

      // Batch fetch product data to check types
      const productTypePromises = productData.map(async ({ item, productId, handle }) => {
        try {
          // Try .js endpoint first (returns product directly)
          const apiUrl = `/products/${handle}.js`;
          const response = await fetch(apiUrl);
          if (!response.ok) return null;

          const data = await response.json();

          // Handle both formats: direct product object or {product: {...}}
          const product = data.product || data;

          return { item, product, productId, handle };
        } catch (error) {
          console.warn(`[PartsSearch] Error fetching product ${handle}:`, error);
          return null;
        }
      });

      const productDataResults = await Promise.all(productTypePromises);
      const queryLower = trimmedQuery.toLowerCase();

      for (const result of productDataResults) {
        if (!result) continue;

        const product = result.product;
        let productType = product.product_type || product.type || '';

        if (!productType && product.variants && product.variants.length > 0) {
          productType = product.variants[0].product_type || '';
        }

        if (!productType || productType.toLowerCase() !== 'parts') {
          continue;
        }

        const productTitle = (product.title || '').toLowerCase();
        const productDescription = (product.description || '').toLowerCase();
        const productVendor = (product.vendor || '').toLowerCase();
        const productTags = Array.isArray(product.tags)
          ? product.tags.map((/** @type {any} */ tag) => String(tag).toLowerCase()).join(' ')
          : '';

        const matchesQuery =
          productTitle.includes(queryLower) ||
          productDescription.includes(queryLower) ||
          productVendor.includes(queryLower) ||
          productTags.includes(queryLower) ||
          this.#variantsMatchQuery(product, queryLower) ||
          result.handle.toLowerCase().includes(queryLower);

        if (matchesQuery) {
          partsProducts.push({
            item: result.item,
            product: result.product,
            productId: result.productId,
            handle: result.handle
          });
        }
      }
      
      // For SKU-like queries, narrow results to prevent substring false positives.
      // Priority 1: exact handle/title/SKU/barcode match.
      // Priority 2: exact tag match (e.g. "DK11C" tag, not "DK11CR30" which contains it as substring).
      // Priority 3: keep all substring matches as fallback (e.g. partial query with no exact tags).
      if (partsProducts.length > 0 && this.#isSkuLikeQuery(trimmedQuery)) {
        const strictIdentifier = partsProducts.filter(({ product, handle }) =>
          this.#hasExactIdentifierMatch(product, handle, queryLower)
        );
        if (strictIdentifier.length > 0) {
          partsProducts = strictIdentifier;
        } else {
          const strictTag = partsProducts.filter(({ product }) =>
            Array.isArray(product.tags) &&
            product.tags.some((/** @type {any} */ tag) => String(tag).toLowerCase() === queryLower)
          );
          if (strictTag.length > 0) {
            partsProducts = strictTag;
          }
        }
      }

      if (gen !== this.#searchGeneration) return;
      await this.#updateResultsList(partsProducts, trimmedQuery, gen);
    } catch (error) {
      // Hide loading animation
      this.#showLoadingAnimation(false);
      // Reset search flag
      this.#isSearching = false;
      this.#showEmptyState(true);
    }
  }

  /**
   * Fallback for SKU-like queries that returned 0 tag results (e.g. part article numbers
   * like "G100.007" that live in title/SKU, not as collection tags).
   *
   * Strategy:
   *  1. Find the product via /search/suggest.json (which searches title + SKU correctly).
   *  2. Use the product's OWN TAGS to build the render URL — e.g. the product has tag
   *     "APE100PTO", so we render /collections/parts/ape100pto which is a small set
   *     (< 48 products) that fits on page 1, guaranteeing the card is in the HTML.
   *  3. Filter the rendered cards by the confirmed product IDs from step 1.
   *
   * This avoids the pagination problem (large collection → product on page 5) AND the
   * false-match problem (collection q=g100.007 returns unrelated products).
   *
   * @param {string} query
   * @param {number} gen
   */
  async #filterBySuggest(query, gen) {
    try {
      // 1. Predictive search — finds products by title and variant SKU
      const suggestUrl = new URL('/search/suggest.json', window.location.origin);
      suggestUrl.searchParams.set('q', query);
      suggestUrl.searchParams.set('resources[type]', 'product');
      suggestUrl.searchParams.set('resources[limit]', '10');
      suggestUrl.searchParams.set('resources[options][fields]', 'title,variants.sku,variants.barcode');
      suggestUrl.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(suggestUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok || gen !== this.#searchGeneration) {
        this.#showLoadingAnimation(false);
        this.#showEmptyState(true);
        this.#isSearching = false;
        return;
      }

      const data = await response.json();
      const suggestedProducts = data?.resources?.results?.products || [];

      if (gen !== this.#searchGeneration) return;
      if (suggestedProducts.length === 0) {
        // No title/SKU match — treat as machine code and show all parts for that machine.
        await this.#filterByTag(query, gen);
        return;
      }

      // 2. Filter to product_type=parts and query match (suggest response has type + title)
      const queryLower = query.toLowerCase();
      const confirmed = suggestedProducts.filter((/** @type {any} */ sp) => {
        const type = (sp.type || sp.product_type || '').toLowerCase();
        if (type !== 'parts') return false;
        const title = (sp.title || '').toLowerCase();
        const handle = sp.handle || this.#extractHandleFromHref(sp.url) || '';
        return title.includes(queryLower) || handle.toLowerCase().includes(queryLower);
      });

      if (gen !== this.#searchGeneration) return;
      if (confirmed.length === 0) {
        // Suggest returned results but none matched type=parts + title/handle —
        // treat as machine code and show all parts for that machine.
        await this.#filterByTag(query, gen);
        return;
      }

      // 3. Build the render URL using the first product's first tag.
      // e.g. product has tag "APE100PTO" → render /collections/parts/ape100pto
      // That collection is small (< 48 items), so the card will be on page 1 — no pagination issues.
      const firstProduct = confirmed[0];
      const firstTag = Array.isArray(firstProduct.tags) && firstProduct.tags.length > 0
        ? String(firstProduct.tags[0]).toLowerCase()
        : null;

      const currentPath = window.location.pathname;
      const collectionMatch = currentPath.match(/\/collections\/([^/]+)/);
      const collectionHandle = collectionMatch ? collectionMatch[1] : 'parts';

      const renderBase = firstTag
        ? `/collections/${collectionHandle}/${encodeURIComponent(firstTag)}`
        : `/collections/${collectionHandle}`;

      const renderUrl = new URL(renderBase, window.location.origin);
      // Preserve active Shopify facet filters (availability, sort, etc.)
      new URL(window.location.href).searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by') renderUrl.searchParams.set(key, value);
      });
      renderUrl.searchParams.set('page', '1');

      const resultsListElement = document.querySelector('results-list');
      const sectionElement = resultsListElement?.closest('.shopify-section');
      const normalizedSectionId = sectionElement?.id || '';
      const sectionId = resultsListElement?.getAttribute('section-id') || '';

      const resultsList = document.querySelector('#ResultsList');
      if (!resultsList) { this.#showLoadingAnimation(false); this.#isSearching = false; return; }
      const productGrid = /** @type {HTMLElement | null} */ (resultsList.querySelector('.product-grid'));
      const emptyState = /** @type {HTMLElement | null} */ (resultsList.querySelector('.main-collection-grid__empty'));

      if (emptyState) emptyState.style.display = 'none';
      // Clear now — prevents hidden original products from being revealed on any failure path
      if (productGrid) productGrid.innerHTML = '';

      const expectedIds = confirmed.map((/** @type {any} */ sp) => String(sp.id || ''));
      const allMatchedItems = [];
      const seenIds = new Set();
      const parser = new DOMParser();

      // Loop pages until the product is found (up to 10 pages = 480 products)
      for (let page = 1; page <= 10; page++) {
        if (gen !== this.#searchGeneration) return;
        renderUrl.searchParams.set('page', String(page));

        let sectionHTML;
        try {
          sectionHTML = await sectionRenderer.getSectionHTML(normalizedSectionId, false, renderUrl);
        } catch { break; }
        if (!sectionHTML) break;

        const doc = parser.parseFromString(sectionHTML, 'text/html');
        const newGrid = doc.querySelector('#ResultsList .product-grid');
        if (!newGrid) break;

        const items = Array.from(newGrid.querySelectorAll('.product-grid__item'));
        if (items.length === 0) break;

        for (const item of items) {
          const id = item.getAttribute('data-product-id');
          if (id && expectedIds.includes(String(id)) && !seenIds.has(id)) {
            seenIds.add(id);
            allMatchedItems.push(item);
          }
        }

        if (allMatchedItems.length >= confirmed.length) break;
      }

      // Also fetch all parts tagged with the query as a machine tag and merge them in.
      // Guard: only run when the query is an EXACT tag on one of the confirmed products.
      // Without this, prefix queries like "APE" would match the non-existent "ape" tag URL,
      // causing Shopify to silently return the full unfiltered collection.
      const queryLowerTag = query.toLowerCase();
      const isActualMachineTag = confirmed.some((/** @type {any} */ sp) =>
        Array.isArray(sp.tags) && sp.tags.some((/** @type {any} */ t) => String(t).toLowerCase() === queryLowerTag)
      );

      if (isActualMachineTag) {
        const machineTagUrl = new URL(
          `/collections/${collectionHandle}/${encodeURIComponent(queryLowerTag)}`,
          window.location.origin
        );
        new URL(window.location.href).searchParams.forEach((value, key) => {
          if (key.startsWith('filter.') || key === 'sort_by') machineTagUrl.searchParams.set(key, value);
        });

        for (let page = 1; page <= 10; page++) {
          if (gen !== this.#searchGeneration) return;
          machineTagUrl.searchParams.set('page', String(page));
          machineTagUrl.searchParams.set('per_page', '50');

          let tagHTML;
          try {
            tagHTML = await sectionRenderer.getSectionHTML(normalizedSectionId, false, machineTagUrl);
          } catch { break; }
          if (!tagHTML) break;

          const tagDoc = parser.parseFromString(tagHTML, 'text/html');
          const tagGrid = tagDoc.querySelector('#ResultsList .product-grid');
          if (!tagGrid) break;

          const tagItems = Array.from(tagGrid.querySelectorAll('.product-grid__item'));
          let newCount = 0;
          for (const item of tagItems) {
            const id = item.getAttribute('data-product-id');
            if (id && !seenIds.has(id)) {
              seenIds.add(id);
              allMatchedItems.push(item);
              newCount++;
            }
          }
          if (newCount === 0) break;
        }
      }

      if (gen !== this.#searchGeneration) return;

      if (allMatchedItems.length === 0) {
        this.#showLoadingAnimation(false);
        this.#showEmptyState(true);
        this.#isSearching = false;
        return;
      }

      if (productGrid) {
        allMatchedItems.forEach((item, index) => {
          const cloned = item.cloneNode(true);
          if (cloned instanceof HTMLElement) {
            cloned.setAttribute('id', `${sectionId}-${cloned.getAttribute('data-product-id')}`);
            cloned.className = `product-grid__item product-grid__item--${index}`;
            productGrid.appendChild(cloned);
          }
        });

        const count = allMatchedItems.length;
        this.#applyClientSearchPaginationState(productGrid, count);
        const perPage = parseInt(productGrid.dataset.displayPerPage || '12', 10);
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination instanceof HTMLElement) {
          customPagination.style.setProperty('display', count <= perPage ? 'none' : '', 'important');
        }
        productGrid.style.setProperty('display', '', 'important');
      }

      document.querySelector('style[data-parts-search-hide]')?.remove();
      this.#isSearching = false;
      this.#showLoadingAnimation(false);
      this.#reinitializeComponents();
    } catch {
      this.#showLoadingAnimation(false);
      this.#showEmptyState(true);
      this.#isSearching = false;
    }
  }

  /**
   * Extracts product handle from a URL or href attribute
   * @param {string} href - The URL or href string
   * @returns {string|null} Product handle
   */
  #extractHandleFromHref(href) {
    if (!href) return null;
    const match = href.match(/\/products\/([^/?]+)/);
    return match && match[1] ? match[1] : null;
  }

  /**
   * Gets product handle from a product item element
   * @param {Element} item - Product item element
   * @returns {string|null} Product handle
   */
  #getProductHandleFromItem(item) {
    const handle = item.getAttribute('data-product-handle');
    if (handle) return handle;

    // Helper to extract handle from element
    const tryExtractFromElement = (/** @type {any} */ element) => {
      if (!element) return null;
      const href = element.getAttribute?.('href') || element.href;
      return href ? this.#extractHandleFromHref(href) : null;
    };

    // Try item itself if it's a link
    if (item instanceof HTMLAnchorElement && item.href?.includes('/products/')) {
      const handle = tryExtractFromElement(item);
      if (handle) return handle;
    }

    // Try various selectors
    const selectors = [
      'a[href*="/products/"]',
      'product-card-link',
      'product-card a[href*="/products/"]'
    ];

    for (const selector of selectors) {
      const element = item.querySelector(selector) || item.closest(selector);
      const handle = tryExtractFromElement(element);
      if (handle) return handle;
    }

    // Try product-card-link href attribute
    const productCardLink = item.querySelector('product-card-link');
    if (productCardLink) {
      const handle = tryExtractFromElement(productCardLink);
      if (handle) return handle;
    }

    return null;
  }
  
  /**
   * Builds the grid from /search HTML nodes when section re-render cannot include those products
   * (e.g. "All Categories" /collections/parts only paginates collection.products — matches may be past page 1).
   * @param {HTMLElement} productGrid
   * @param {Array<{item: Element, product: any, productId: string}>} productsData
   * @param {string} sectionId
   */
  #fillGridFromSearchResultItems(productGrid, productsData, sectionId) {
    productGrid.innerHTML = '';
    productsData.forEach(({ item, productId }, index) => {
      const clonedItem = item.cloneNode(true);
      if (clonedItem instanceof HTMLElement) {
        clonedItem.setAttribute('id', `${sectionId}-${productId}`);
        clonedItem.setAttribute('class', `product-grid__item product-grid__item--${index}`);
        clonedItem.setAttribute('data-page', '1');
        clonedItem.setAttribute('data-product-id', productId);
        productGrid.appendChild(clonedItem);
      }
    });
  }
  
  /**
   * After client-side search the grid still had the old collection pagination; results-list keeps prefetched
   * pages and infinite scroll then appends real collection page 2+ (wrong products). Reset both.
   * @param {HTMLElement} productGrid
   * @param {number} visibleCount
   */
  #applyClientSearchPaginationState(productGrid, visibleCount) {
    productGrid.dataset.lastPage = '1';
    productGrid.dataset.currentPage = '1';
    productGrid.dataset.paginateItems = String(visibleCount);
    productGrid.dataset.totalProducts = String(visibleCount);

    const rl = document.querySelector('results-list');
    if (rl) {
      const pages = /** @type {{ pages?: Map<unknown, unknown> }} */ (rl).pages;
      if (pages instanceof Map) {
        pages.clear();
      }
    }
  }

  /**
   * Updates the ResultsList with search results
   * @param {Array<{item: Element, product: any, productId: string}>} productsData - Array of {item, product, productId} objects
   * @param {string} query - The search query
   * @param {number} gen - #searchGeneration when this run started
   */
  async #updateResultsList(productsData, query, gen) {
    if (gen !== this.#searchGeneration) return;

    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) {
      this.#showLoadingAnimation(false);
      this.#isSearching = false;
      return;
    }

    const productGrid = resultsList.querySelector('.product-grid');
    const emptyState = resultsList.querySelector('.main-collection-grid__empty');

    if (productsData.length === 0) {
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = 'none';
      }
      this.#showLoadingAnimation(false);
      this.#showEmptyState(true);
      this.#isSearching = false;
      return;
    }

    // Hide empty state first
    if (emptyState instanceof HTMLElement) {
      emptyState.style.display = 'none';
    }

    // Show product grid
    if (productGrid instanceof HTMLElement) {
      productGrid.style.display = '';
      productGrid.innerHTML = '';
    }

    // Get section ID
    const resultsListElement = document.querySelector('results-list');
    const sectionId = resultsListElement?.getAttribute('section-id') || '';

    // Find the section element to get its ID for sectionRenderer
    const sectionElement = resultsListElement?.closest('.shopify-section');
    const normalizedSectionId = sectionElement?.id || '';

    // Build the render URL WITHOUT the search query.
    // Shopify's collection `q` param is a title-only search — it would exclude products that
    // match only via tags/SKU, which is the primary matching mechanism for parts.
    // We've already identified the correct product IDs via /search above; here we just need
    // the section to return as many collection product cards as possible so we can pick the
    // right ones by ID. Any active facet filters (availability, etc.) are intentionally
    // preserved so the rendered cards reflect the current filter state.
    const currentUrl = new URL(window.location.href);
    const renderUrl = new URL(currentUrl.pathname, window.location.origin);
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'q' && key !== 'section_id') {
        renderUrl.searchParams.set(key, value);
      }
    });
    // Always use page 1 and a large per_page to maximise the pool of product cards
    // available for ID matching. Without this, products beyond the default ~20-per-page
    // limit are invisible to the matcher and silently dropped from search results.
    renderUrl.searchParams.set('page', '1');
    renderUrl.searchParams.set('per_page', '200');

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(normalizedSectionId, false, renderUrl);

      if (gen !== this.#searchGeneration) return;

      if (!sectionHTML) {
        if (productGrid instanceof HTMLElement) {
          productGrid.style.display = 'none';
        }
        this.#showLoadingAnimation(false);
        this.#showEmptyState(true);
        this.#isSearching = false;
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const newResultsList = doc.querySelector('#ResultsList');

      if (!newResultsList) {
        if (productGrid instanceof HTMLElement) {
          productGrid.style.display = 'none';
        }
        this.#showLoadingAnimation(false);
        this.#showEmptyState(true);
        this.#isSearching = false;
        return;
      }

      const newProductGrid = newResultsList.querySelector('.product-grid');

      if (!newProductGrid || !(productGrid instanceof HTMLElement)) {
        if (productGrid instanceof HTMLElement) {
          productGrid.style.display = 'none';
        }
        this.#showLoadingAnimation(false);
        this.#showEmptyState(true);
        this.#isSearching = false;
        return;
      }

      const productItems = Array.from(newProductGrid.querySelectorAll('.product-grid__item'));
      const filteredItems = [];
      const expectedProductIds = productsData.map(p => String(p.productId));

      for (const item of productItems) {
        const itemProductId = item.getAttribute('data-product-id');
        if (itemProductId && expectedProductIds.includes(String(itemProductId))) {
          filteredItems.push(item);
        }
      }


      if (filteredItems.length === 0 && productGrid instanceof HTMLElement) {
        // Collection section HTML lists paginated collection.products; `q` does not narrow Liquid output.
        // On /collections/parts matches from /search are often not on page 1 — use cards from the search response.
        this.#fillGridFromSearchResultItems(productGrid, productsData, sectionId);
      } else if (filteredItems.length > 0 && productGrid instanceof HTMLElement) {
        productGrid.innerHTML = '';
        filteredItems.forEach((item, index) => {
          const clonedItem = item.cloneNode(true);
          if (clonedItem instanceof HTMLElement) {
            clonedItem.setAttribute('id', `${sectionId}-${clonedItem.getAttribute('data-product-id')}`);
            clonedItem.className = `product-grid__item product-grid__item--${index}`;
            productGrid.appendChild(clonedItem);
          }
        });
      }
    } catch (error) {
      if (gen !== this.#searchGeneration) return;
      if (productGrid instanceof HTMLElement) {
        this.#fillGridFromSearchResultItems(productGrid, productsData, sectionId);
      }
    }
    
    if (gen !== this.#searchGeneration) return;

    // Reset search flag - search is complete
    this.#isSearching = false;

    // Hide loading animation
    this.#showLoadingAnimation(false);

    // Remove any inline styles that were added to hide products
    // Remove the style element that was added by the inline script
    const hideStyle = document.querySelector('style[data-parts-search-hide]');
    if (hideStyle) {
      hideStyle.remove();
    }

    // Show the grid after search is complete (after resetting flag to prevent MutationObserver from hiding it)
    if (productGrid instanceof HTMLElement) {
      // Override any inline styles with !important to ensure products are shown
      productGrid.style.setProperty('display', '', 'important');
      // Also remove inline style attribute if it was set by Liquid template
      if (productGrid.getAttribute('style') === 'display: none !important;') {
        productGrid.removeAttribute('style');
      }
    }

    const customPagination = document.querySelector('custom-pagination');
    if (productGrid instanceof HTMLElement && query.trim()) {
      const visibleCount = productGrid.querySelectorAll('.product-grid__item').length;
      this.#applyClientSearchPaginationState(productGrid, visibleCount);
      const perPage = parseInt(productGrid.dataset.displayPerPage || '12', 10);
      if (customPagination instanceof HTMLElement) {
        if (visibleCount <= perPage) {
          customPagination.style.setProperty('display', 'none', 'important');
        } else {
          customPagination.style.setProperty('display', '', 'important');
          if (customPagination.getAttribute('style') === 'display: none !important;') {
            customPagination.removeAttribute('style');
          }
        }
      }
    } else if (customPagination instanceof HTMLElement) {
      customPagination.style.setProperty('display', '', 'important');
      if (customPagination.getAttribute('style') === 'display: none !important;') {
        customPagination.removeAttribute('style');
      }
    }

    // Reinitialize components
    this.#reinitializeComponents();
  }

  /**
   * Reinitializes components after updating the grid
   */
  #reinitializeComponents() {
    window.dispatchEvent(new CustomEvent('products-loaded', {
      detail: { count: document.querySelectorAll('.product-grid__item').length }
    }));
  }

  /**
   * Updates the URL with search query
   * @param {string} query - The search query
   */
  #updateURL(query) {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
      // Reset to page 1 when searching
      url.searchParams.set('page', '1');
    } else {
      url.searchParams.delete('q');
    }
    history.pushState({}, '', url.toString());
  }

  /**
   * Shows or hides the loading animation
   * @param {boolean} show - Whether to show the loading animation
   */
  #showLoadingAnimation(show) {
    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) return;

    let loadingAnimation = resultsList.querySelector('.parts-search-loading');
    const customPagination = document.querySelector('custom-pagination');
    const emptyState = resultsList.querySelector('.main-collection-grid__empty');

    if (show) {
      if (customPagination instanceof HTMLElement) {
        customPagination.style.display = 'none';
      }

      if (emptyState instanceof HTMLElement) {
        emptyState.style.display = 'none';
      }

      if (!loadingAnimation) {
        const newLoadingAnimation = document.createElement('div');
        newLoadingAnimation.className = 'parts-search-loading';
        newLoadingAnimation.style.cssText = `
          grid-column: 1 / -1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          min-height: 200px;
        `;

        const spinner = document.createElement('div');
        spinner.style.cssText = `
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(90deg, #ffffff 0%, #ff6600 50%, #ffffff 100%);
          background-size: 200% 100%;
          animation: parts-search-loading 1.5s ease-in-out infinite;
        `;

        newLoadingAnimation.appendChild(spinner);
        loadingAnimation = newLoadingAnimation;

        // Add CSS animation if not already added
        if (!document.getElementById('parts-search-loading-styles')) {
          const style = document.createElement('style');
          style.id = 'parts-search-loading-styles';
          style.textContent = `
            @keyframes parts-search-loading {
              0% {
                background-position: 200% 0;
              }
              100% {
                background-position: -200% 0;
              }
            }
          `;
          document.head.appendChild(style);
        }

        if (resultsList instanceof HTMLElement) {
          resultsList.appendChild(loadingAnimation);
        }
      } else if (loadingAnimation instanceof HTMLElement) {
        loadingAnimation.style.display = 'flex';
      }
    } else {
      if (loadingAnimation instanceof HTMLElement) {
        loadingAnimation.style.display = 'none';
      }
      if (customPagination instanceof HTMLElement) {
        customPagination.style.display = '';
      }
    }
  }

  /**
   * Shows or hides the empty state
   * @param {boolean} show - Whether to show the empty state
   */
  #showEmptyState(show) {
    const resultsList = document.querySelector('#ResultsList');
    if (!resultsList) return;

    const productGrid = resultsList.querySelector('.product-grid');
    let emptyState = resultsList.querySelector('.main-collection-grid__empty');
    const customPagination = document.querySelector('custom-pagination');

    const getCollectionUrl = () => {
      const currentPath = window.location.pathname;
      const pathWithoutQuery = currentPath.split('?')[0] || currentPath;
      return currentPath.includes('/collections/') ? pathWithoutQuery : '/collections/parts';
    };

    if (show) {
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = 'none';
      }

      if (customPagination instanceof HTMLElement) {
        customPagination.style.display = 'none';
      }

      if (!emptyState) {
        const newEmptyState = document.createElement('div');
        newEmptyState.className = 'main-collection-grid__empty';
        if (newEmptyState instanceof HTMLElement) {
          newEmptyState.style.gridColumn = '1 / -1';
          const collectionUrl = getCollectionUrl();
          newEmptyState.innerHTML = `
            <h2 class="main-collection-grid__empty-title h2">No products found</h2>
            <p>Try using fewer filters, or <a class="main-collection-grid__empty-link" href="${collectionUrl}">clear all filters</a>.</p>
          `;
          if (resultsList instanceof HTMLElement) {
            resultsList.appendChild(newEmptyState);
          }
        }
        emptyState = newEmptyState;
      } else if (emptyState instanceof HTMLElement) {
        emptyState.style.gridColumn = '1 / -1';
        emptyState.style.display = '';
        const link = emptyState.querySelector('.main-collection-grid__empty-link');
        if (link instanceof HTMLAnchorElement) {
          link.setAttribute('href', getCollectionUrl());
        }
      }
    } else {
      if (emptyState instanceof HTMLElement) {
        emptyState.style.display = 'none';
      }
      if (productGrid instanceof HTMLElement) {
        productGrid.style.display = '';
      }
      if (customPagination instanceof HTMLElement) {
        customPagination.style.display = '';
      }
    }
  }
}

if (!customElements.get('parts-search-component')) {
  customElements.define('parts-search-component', PartsSearchComponent);
}

// Handle category selector filtering by metafield
class CategorySelectorComponent extends Component {
  #mql = window.matchMedia('(min-width: 390px)');
  #mqlHandler = () => {
    const checked = this.querySelector('input[type="radio"][name="collection_handle"]:checked');
    const handle = checked instanceof HTMLInputElement ? checked.value : '';
    this.#updateLabel(handle);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#mql.addEventListener('change', this.#mqlHandler);

    // Get current collection handle from the page URL
    const currentPath = window.location.pathname;
    const collectionMatch = currentPath.match(/\/collections\/([^\/]+)/);
    const currentCollectionHandle = collectionMatch ? collectionMatch[1] : '';

    // If current collection is "parts" or empty, treat it as "All Categories"
    const isAllCategories = !currentCollectionHandle || currentCollectionHandle === 'parts';

    if (isAllCategories) {
      // Set "All Categories" as selected
      this.#setSelectedCategory('');
      this.#updateLabel('');
    } else if (currentCollectionHandle) {
      this.#setSelectedCategory(currentCollectionHandle);
      // Update label to show selected category on page load
      this.#updateLabel(currentCollectionHandle);
    }

    const select = this.querySelector('select[name="collection_handle"]');
    if (select instanceof HTMLSelectElement) {
      if (isAllCategories) {
        select.value = '';
      } else if (currentCollectionHandle) {
        select.value = currentCollectionHandle;
      }
      select.addEventListener('change', (e) => {
        if (e.target instanceof HTMLSelectElement) {
          const collectionHandle = e.target.value;
          this.#handleCategoryChange(collectionHandle);
        }
      });
    }

    // Handle radio button changes
    const radioInputs = this.querySelectorAll('input[type="radio"][name="collection_handle"]');
    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (isAllCategories && input.value === '') {
          input.checked = true;
        } else if (currentCollectionHandle && input.value === currentCollectionHandle) {
          input.checked = true;
        }
        input.addEventListener('change', (e) => {
          if (e.target instanceof HTMLInputElement && e.target.checked) {
            const collectionHandle = e.target.value;
            this.#handleCategoryChange(collectionHandle);
          }
        });
      }
    });
  }

  /**
   * Handles category change - navigates to collection page
   * @param {string} collectionHandle - The collection handle to navigate to
   */
  #handleCategoryChange(collectionHandle) {
    // Get current per_page parameter from URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentPerPage = urlParams.get('per_page');

    // Preserve per_page if it's 24 or 48
    let targetUrl = '';

    // If empty (All Categories), navigate to /collections/parts
    if (!collectionHandle || collectionHandle === '') {
      targetUrl = '/collections/parts';
    } else {
      // Find the collection URL from the input element
      const selectedInput = this.querySelector(`input[type="radio"][value="${collectionHandle}"]`);
      if (selectedInput) {
        const collectionUrl = selectedInput.getAttribute('data-collection-url');
        if (collectionUrl) {
          targetUrl = collectionUrl;
        }
      }
    }

    if (targetUrl) {
      // Add per_page parameter if it's 24 or 48
      if (currentPerPage === '24' || currentPerPage === '48') {
        const url = new URL(targetUrl, window.location.origin);
        url.searchParams.set('per_page', currentPerPage);
        // Reset page to 1 when changing category
        url.searchParams.set('page', '1');
        window.location.href = url.toString();
      } else {
        // Reset page to 1 when changing category
        const url = new URL(targetUrl, window.location.origin);
        url.searchParams.set('page', '1');
        window.location.href = url.toString();
      }
    }
  }


  /**
   * Sets the selected category in the UI
   * @param {string} categoryHandle - The category handle (empty string for "All Categories")
   */
  #setSelectedCategory(categoryHandle) {
    // Update select element
    const select = this.querySelector('select[name="collection_handle"]');
    if (select instanceof HTMLSelectElement) {
      select.value = categoryHandle || '';
    }

    // Update radio buttons
    const radioInputs = this.querySelectorAll('input[type="radio"][name="collection_handle"]');
    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        // If categoryHandle is empty or "parts", check the "All Categories" option (value="")
        const shouldCheck = !categoryHandle || categoryHandle === 'parts'
          ? input.value === ''
          : input.value === categoryHandle;
        input.checked = shouldCheck;

        // Update aria-selected
        const option = input.closest('[role="option"]');
        if (option instanceof HTMLElement) {
          option.setAttribute('aria-selected', input.checked ? 'true' : 'false');
          option.tabIndex = input.checked ? 0 : -1;
        }
      }
    });

    // Close dropdown
    const details = this.querySelector('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mql.removeEventListener('change', this.#mqlHandler);
  }

  #truncateText(text) {
    if (!text) return '';
    const limit = window.innerWidth < 390 ? 16 : 21;
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  /**
   * Updates the label text
   * @param {string} categoryHandle - The category handle
   */
  #updateLabel(categoryHandle) {
    const summary = this.querySelector('summary');
    if (!summary) return;

    const label = summary.querySelector('.facets__label');
    const status = summary.querySelector('.facets__status');

    if (!categoryHandle || categoryHandle === '') {
      const labelText = 'All Categories';
      if (label) label.textContent = labelText;
      if (status) status.textContent = '';
      return;
    }

    // Find the category name from the option (already without "(Parts)" suffix)
    const selectedInput = this.querySelector(`input[type="radio"][value="${categoryHandle}"]`);
    if (selectedInput) {
      const optionName = selectedInput.getAttribute('data-option-name') || '';
      const truncatedName = this.#truncateText(optionName);
      if (label) label.textContent = truncatedName;
      if (status) status.textContent = optionName;
    } else {
      // Fallback: if input not found, try to get from select
      const select = this.querySelector('select[name="collection_handle"]');
      if (select instanceof HTMLSelectElement) {
        const selectedOption = select.querySelector(`option[value="${categoryHandle}"]`);
        if (selectedOption) {
          const optionName = selectedOption.textContent.trim();
          const truncatedName = this.#truncateText(optionName);
          if (label) label.textContent = truncatedName;
          if (status) status.textContent = optionName;
        }
      }
    }
  }

}

if (!customElements.get('category-selector-component')) {
  customElements.define('category-selector-component', CategorySelectorComponent);
}


// Handle products per page selector
class ProductsPerPageSelectorComponent extends Component {
  #rendering = false;
  /** @type {MutationObserver | null} */
  #selfObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Get initial per_page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialPerPage = urlParams.get('per_page') || '12';
    if (initialPerPage) {
      this.#setSelectedPerPage(initialPerPage);
    }

    const select = this.querySelector('select[name="per_page"]');
    if (select instanceof HTMLSelectElement) {
      if (initialPerPage) {
        select.value = initialPerPage;
      }
      select.addEventListener('change', (e) => {
        if (e.target instanceof HTMLSelectElement) {
          const perPage = e.target.value;
          this.#handlePerPageChange(perPage);
        }
      });
    }

    // Handle radio button changes
    const radioInputs = this.querySelectorAll('input[type="radio"][name="per_page"]');
    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (initialPerPage && input.value === initialPerPage) {
          input.checked = true;
        }
        input.addEventListener('change', (e) => {
          if (e.target instanceof HTMLInputElement && e.target.checked) {
            const perPage = e.target.value;
            this.#handlePerPageChange(perPage);
          }
        });
      }
    });

    // When sectionRenderer morphs this element in place it resets the label to the
    // Liquid-rendered default (12) even though the URL still has the correct per_page.
    // Detect any subtree change and re-sync from the actual URL.
    this.#selfObserver = new MutationObserver(() => {
      if (this.#rendering) return;
      this.#syncFromUrl();
    });
    this.#selfObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selfObserver?.disconnect();
    this.#selfObserver = null;
  }

  #syncFromUrl() {
    this.#rendering = true;
    const urlParams = new URLSearchParams(window.location.search);
    const perPage = urlParams.get('per_page') || '12';
    this.#setSelectedPerPage(perPage);
    setTimeout(() => { this.#rendering = false; }, 0);
  }

  /**
   * Handles per page change - updates URL and reloads page
   * @param {string} perPage - The number of products per page
   */
  #handlePerPageChange(perPage) {
    this.#setSelectedPerPage(perPage);
    this.#updateURL(perPage);
    // Dispatch event before reload to notify custom pagination
    window.dispatchEvent(new CustomEvent('per-page-changed', { detail: { perPage } }));
    // Reload page to apply new pagination
    window.location.reload();
  }

  /**
   * Sets the selected per page in the UI
   * @param {string} perPage - The per page value
   */
  #setSelectedPerPage(perPage) {
    // Update select element
    const select = this.querySelector('select[name="per_page"]');
    if (select instanceof HTMLSelectElement) {
      select.value = perPage;
    }

    // Update radio buttons
    const radioInputs = this.querySelectorAll('input[type="radio"][name="per_page"]');
    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === perPage;

        // Update aria-selected
        const option = input.closest('[role="option"]');
        if (option instanceof HTMLElement) {
          option.setAttribute('aria-selected', input.checked ? 'true' : 'false');
          option.tabIndex = input.checked ? 0 : -1;
        }
      }
    });

    // Update label
    this.#updateLabel(perPage);

    // Close dropdown
    const details = this.querySelector('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /**
   * Updates the label text
   * @param {string} perPage - The per page value
   */
  #updateLabel(perPage) {
    const summary = this.querySelector('summary');
    if (!summary) return;

    const label = summary.querySelector('.facets__label');
    const status = summary.querySelector('.facets__status');

    const labelText = `Show: ${perPage} products`;

    if (label) {
      label.textContent = labelText;
    }
    if (status) {
      status.textContent = labelText;
    }
  }

  /**
   * Updates the URL with per_page parameter
   * @param {string} perPage - The per page value
   */
  #updateURL(perPage) {
    const url = new URL(window.location.href);
    if (perPage && perPage !== '12') {
      url.searchParams.set('per_page', perPage);
    } else {
      url.searchParams.delete('per_page');
    }
    // Reset page to 1 when changing per_page
    url.searchParams.set('page', '1');
    history.pushState({}, '', url.toString());
  }
}

if (!customElements.get('products-per-page-selector-component')) {
  customElements.define('products-per-page-selector-component', ProductsPerPageSelectorComponent);
}
